import { useEffect, useRef, useState } from 'react';

/**
 * Hook personnalisé pour les animations au scroll
 * Utilise l'Intersection Observer API pour détecter quand un élément entre dans le viewport
 *
 * @param {Object} options - Options de configuration
 * @param {number} options.threshold - Pourcentage de visibilité requis (0-1)
 * @param {string} options.rootMargin - Marge autour du viewport
 * @param {boolean} options.triggerOnce - Si true, l'animation ne se déclenche qu'une fois
 * @returns {Object} - { ref, isVisible }
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Vérifier si l'utilisateur préfère les animations réduites
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

/**
 * Hook pour animer plusieurs éléments avec un effet stagger (décalage)
 *
 * @param {number} itemCount - Nombre d'éléments à animer
 * @param {Object} options - Options de configuration
 * @param {number} options.staggerDelay - Délai entre chaque élément (ms)
 * @returns {Object} - { containerRef, isContainerVisible, getItemStyle }
 */
export const useStaggerAnimation = (itemCount, options = {}) => {
  const {
    staggerDelay = 150,
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px'
  } = options;

  const { ref: containerRef, isVisible: isContainerVisible } = useScrollAnimation({
    threshold,
    rootMargin,
    triggerOnce: true
  });

  const getItemStyle = (index) => ({
    opacity: isContainerVisible ? 1 : 0,
    transform: isContainerVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 0.6s ease-out, transform 0.6s ease-out`,
    transitionDelay: isContainerVisible ? `${index * staggerDelay}ms` : '0ms'
  });

  return { containerRef, isContainerVisible, getItemStyle };
};

/**
 * Hook pour animer un compteur de 0 à une valeur cible
 *
 * @param {number} target - Valeur cible
 * @param {Object} options - Options de configuration
 * @param {number} options.duration - Durée de l'animation (ms)
 * @param {boolean} options.startOnVisible - Démarrer quand visible
 * @returns {Object} - { ref, count, isVisible }
 */
export const useCounterAnimation = (target, options = {}) => {
  const {
    duration = 2000,
    startOnVisible = true
  } = options;

  const { ref, isVisible } = useScrollAnimation({ triggerOnce: true });
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!startOnVisible || !isVisible || hasAnimated.current) return;

    hasAnimated.current = true;
    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function: easeOutExpo
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

      const currentValue = Math.floor(startValue + (target - startValue) * easeProgress);
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration, startOnVisible]);

  return { ref, count, isVisible };
};

export default useScrollAnimation;
